import React, { useState } from 'react';

import { Box, Button, Collapse, IconButton } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { FiChevronDown } from 'react-icons/fi';
import clsx from 'clsx';

import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

import { ModuleDraft, ModulePermissionState, VisibleStaffModule } from '../types';
import { useRolesStyles } from '../styles';
import { CatalogueIndex, setsEqual, toDisplayName } from '../utils/permissionState';
import { useResourcePermissionExceptions } from '../hooks/useResourcePermissionExceptions';
import { PermissionCheckList } from './PermissionCheckList';
import { PermissionDiffBanner } from './PermissionDiffBanner';
import { ScopeSelector } from './ScopeSelector';

interface Props {
  companyId?: string;
  userId: string;
  module: VisibleStaffModule;
  draft: ModuleDraft;
  permissionState: ModulePermissionState;
  effective: Set<string>;
  catalogue: CatalogueIndex | null;
  disabled?: boolean;
  isOwner?: boolean;
  open: boolean;
  onResourceToggle: (id: string, checked: boolean) => void;
  onSelectAllResources: (checked: boolean) => void;
  onPermissionToggle: (systemName: string, checked: boolean) => void;
  onReset: () => void;
  onClearResourceOverridesChange: (value: boolean) => void;
  onExceptionsLoaded?: (hasExceptions: boolean) => void;
}

export const ModulePermissionPanel: React.FC<Props> = ({
  companyId,
  userId,
  module,
  draft,
  permissionState,
  effective,
  catalogue,
  disabled,
  isOwner,
  open,
  onResourceToggle,
  onSelectAllResources,
  onPermissionToggle,
  onReset,
  onClearResourceOverridesChange,
  onExceptionsLoaded,
}) => {
  const classes = useRolesStyles();
  const [expandedResourceId, setExpandedResourceId] = useState<string | null>(null);

  const exceptions = useResourcePermissionExceptions({
    companyId,
    userId,
    moduleSystemName: module.systemName,
    catalogue,
    // An owner can't have per-resource exceptions — the endpoint 422s on them.
    enabled: open && !!module.resourceKind && !isOwner,
  });

  React.useEffect(() => {
    onExceptionsLoaded?.(exceptions.hasAnyException);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exceptions.hasAnyException]);

  if (!catalogue) {
    return (
      <div className={classes.moduleDetail}>
        <Skeleton variant="rect" height={72} style={{ borderRadius: 10 }} />
      </div>
    );
  }

  // An owner holds everything by construction, so show every box ticked rather
  // than whatever the overrides row happens to say — and never diff it.
  const ownerSet = isOwner ? new Set(module.permissions.map(permission => permission.system_name)) : null;
  const shownEffective = ownerSet || effective;
  const explicitSet = ownerSet || new Set(permissionState.explicit);
  const shownDefaults = ownerSet ? Array.from(ownerSet) : permissionState.roleDefaults;
  const conflictNames = new Set(permissionState.conflicts.flatMap(pair => [pair.permission, pair.requires]));
  const changedFromDefaults = !isOwner && !setsEqual(effective, permissionState.roleDefaults);
  const resolveLabel = (systemName: string) => toDisplayName(systemName, catalogue.index);

  const orderedPermissionNames = module.permissions.map(permission => permission.system_name);

  const renderExceptionRight = (resourceId: string) => {
    const item = exceptions.items[resourceId];
    if (!item) return null;
    const expanded = expandedResourceId === resourceId;
    return (
      <>
        {item.has_exception && <span className={classes.exceptionBadge}>Exception</span>}
        <IconButton
          size="small"
          className={classes.detailToggle}
          onClick={() => setExpandedResourceId(expanded ? null : resourceId)}>
          <span className={clsx(classes.chevron, expanded && classes.chevronOpen)}>
            <FiChevronDown size={14} />
          </span>
        </IconButton>
      </>
    );
  };

  const renderExceptionDetail = (resourceId: string) => {
    if (expandedResourceId !== resourceId) return null;
    const resourceEffective = exceptions.effectiveByResource[resourceId] || new Set<string>();
    const resourceExplicit = new Set(exceptions.explicit[resourceId] || []);
    const resourceConflicts = exceptions.conflicts[resourceId] || [];
    const dirty = exceptions.isResourceDirty(resourceId);

    return (
      <div className={classes.resourceDetail}>
        <div className={classes.sectionLabel}>Exception for this {module.label.toLowerCase()} row</div>
        {!!resourceConflicts.length && (
          <div className={clsx(classes.banner, classes.bannerError)}>
            <div className={classes.bannerText}>
              {resourceConflicts
                .map(pair => `${resolveLabel(pair.permission)} needs ${resolveLabel(pair.requires)}`)
                .join('; ')}
            </div>
          </div>
        )}
        <PermissionCheckList
          groups={module.groups}
          effective={resourceEffective}
          explicit={resourceExplicit}
          defaults={exceptions.moduleDefault}
          added={exceptions.items[resourceId]?.added}
          removed={exceptions.items[resourceId]?.removed}
          conflictNames={new Set(resourceConflicts.flatMap(pair => [pair.permission, pair.requires]))}
          disabled={disabled}
          catalogue={catalogue}
          onToggle={(systemName, checked) => exceptions.togglePermission(resourceId, systemName, checked)}
        />
        {!disabled && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" marginTop={1}>
            <Button size="small" onClick={() => exceptions.resetResource(resourceId)}>
              Match module default
            </Button>
            <Box marginLeft={1}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={!dirty || exceptions.savingId === resourceId}
                onClick={() => exceptions.saveResource(resourceId, orderedPermissionNames)}>
                <ButtonTitleWithLoading
                  title="Save exception"
                  loaderVariant="paper"
                  loading={exceptions.savingId === resourceId}
                />
              </Button>
            </Box>
          </Box>
        )}
      </div>
    );
  };

  return (
    <Collapse in={open}>
      <div className={classes.moduleDetail}>
        {!!module.resourceKind && module.resources.length > 0 && (
          <>
            <div className={classes.sectionLabel}>Access scope — what this role applies to</div>
            <ScopeSelector
              label={module.resourceLabel || 'Resources'}
              resources={module.resources}
              selectedIds={draft.resourceIds}
              disabled={disabled}
              onToggle={onResourceToggle}
              onSelectAll={onSelectAllResources}
              renderItemRight={isOwner || exceptions.loading ? undefined : renderExceptionRight}
              renderItemDetail={isOwner || exceptions.loading ? undefined : renderExceptionDetail}
            />
          </>
        )}

        {module.permissions.length > 0 && (
          <div className={module.resourceKind ? classes.permissionSection : undefined}>
            <div className={classes.sectionLabel}>
              {!isOwner && draft.role?.role_name ? `${draft.role.role_name} — permissions` : 'Permissions'}
            </div>
            <PermissionCheckList
              groups={module.groups}
              effective={shownEffective}
              explicit={explicitSet}
              defaults={shownDefaults}
              added={isOwner ? [] : permissionState.added}
              removed={isOwner ? [] : permissionState.removed}
              conflictNames={conflictNames}
              disabled={disabled || isOwner}
              catalogue={catalogue}
              onToggle={onPermissionToggle}
            />
            {(changedFromDefaults || !!permissionState.conflicts.length) && (
              <PermissionDiffBanner
                roleName={draft.role?.role_name || 'this role'}
                resourceLabel={module.resourceKind ? module.resourceLabel : undefined}
                clearResourceOverrides={permissionState.clearResourceOverrides}
                hasExceptions={exceptions.hasAnyException}
                conflicts={permissionState.conflicts}
                disabled={disabled}
                resolveLabel={resolveLabel}
                onClearResourceOverridesChange={onClearResourceOverridesChange}
                onReset={onReset}
              />
            )}
          </div>
        )}
      </div>
    </Collapse>
  );
};
